import { useState, useCallback, useEffect } from 'react';
import { IconButton, Typography, Button, Tooltip } from '@mui/material';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { containedButtonStyle, textButtonStyle } from '@/theme';
import { useUpdatePaintRegionId } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/hooks/useUpdatePaintRegionId';
import { abyssColors } from '@/theme/colors';
import { tooltipStyles } from './styles';

type CustomTableIconColumnProps = {
  selectedRowIds: string[];
  icon?: JSX.Element;
  dialogTitle: string | undefined | null;
  dialogContent: string | undefined | null;
  action: string | undefined | null;
  tooltipText?: string | undefined | null;
};

export const CustomTableIconColumn = ({
  selectedRowIds,
  icon,
  dialogTitle,
  dialogContent,
  action,
  tooltipText,
}: CustomTableIconColumnProps) => {
  const [openDialog, setOpenDialog] = useState(false);

  const { isRetrying, handleAddPaintRegionId } = useUpdatePaintRegionId(selectedRowIds);

  const handleIconClick = useCallback(() => {
    setOpenDialog(true);
  }, []);

  const handleConfirm = useCallback(
    (confirmAction: string) => {
      if (confirmAction === 'updatePaintRegionId') {
        handleAddPaintRegionId();
      }
    },
    [handleAddPaintRegionId]
  );

  useEffect(() => {
    if (!isRetrying) {
      setOpenDialog(false);
    }
  }, [isRetrying]);

  return (
    <>
      {tooltipText ? (
        <Tooltip
          title={tooltipText}
          disableHoverListener={false}
          arrow
          placement="bottom"
          PopperProps={{ sx: tooltipStyles }}
        >
          <span>
            <IconButton onClick={handleIconClick}>{icon}</IconButton>
          </span>
        </Tooltip>
      ) : (
        <IconButton onClick={handleIconClick}>{icon}</IconButton>
      )}
      {dialogTitle && dialogContent && (
        <GenericDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          title={dialogTitle}
          content={
            <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
              {dialogContent}
            </Typography>
          }
          actions={
            <>
              <Button variant="text" onClick={() => setOpenDialog(false)} sx={textButtonStyle}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => handleConfirm(action ?? '')}
                sx={containedButtonStyle}
                disabled={isRetrying || selectedRowIds.length === 0}
              >
                {isRetrying ? 'Retrying...' : 'Try Again'}
              </Button>
            </>
          }
          actionsStyles={{ mr: 3, pb: 3 }}
        />
      )}
    </>
  );
};
